@file:OptIn(ExperimentalMaterial3Api::class)

package us.laantech.admin.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.text.input.TextFieldState
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.lifecycle.ViewModel
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.lifecycle.viewmodel.compose.viewModel
import androidx.lifecycle.viewmodel.initializer
import androidx.lifecycle.viewmodel.viewModelFactory
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.serialization.json.*
import us.laantech.admin.util.emptyJsonObjectFor
import us.laantech.admin.util.serializableToMap
import us.laantech.common.dto.company.BillingCycle
import us.laantech.common.dto.company.ParentCompany
import us.laantech.common.dto.InternalProductDTO
import us.laantech.common.dto.company.InternalProductLicenseDTO
import us.laantech.common.ui.decimalInputTransformation
import us.laantech.common.ui.twoDecimalOutputTransformation
import us.laantech.common.util.dollarStringToCents
import us.laantech.common.util.generateUUIDv7
import us.laantech.common.util.tryFormatAsDateTime
import us.laantech.common.api.KtorClient
import kotlin.time.Clock

data class CreateEditClientUiState(

//    val internalClientDTO: ParentCompany? = null,

    val formValues: Map<String, JsonElement> = emptyJsonObjectFor(
        ParentCompany.serializer().descriptor
    ).toMap(),
//    val productLicenses: List<InternalProductLicenseDTO> = emptyList(),

    val errorMessage: String? = null,

    // Modal
    val internalProducts: List<InternalProductDTO> = emptyList(),
    val showAddProductLicenseModal: Boolean = false,

    val editMode: Boolean = false,
)

@Composable
fun CreateEditClientView(
    viewModel: CreateEditClientViewModel = viewModel(
        factory = viewModelFactory {
            initializer { CreateEditClientViewModel() }
        }
    ),
    accountSub: String? = null,
    initialClient: ParentCompany? = null,
    onSubmitSuccess: () -> Unit = {},
    ) {
    val uiState by viewModel.uiState.collectAsStateWithLifecycle()
    LaunchedEffect(accountSub, initialClient) {
        viewModel.loadProducts()
        if (initialClient != null) {
            viewModel.initForEdit(initialClient)
        } else {
            accountSub?.let { viewModel.setAccountSub(it) }
        }
    }

    Column {
        uiState.errorMessage?.let { Text("Error: $it") }

        SerializableForm(
            descriptor = ParentCompany.serializer().descriptor,
            values = uiState.formValues,
            onChange = viewModel::onFieldsChanged,
        )


        // Cards
        val json = Json { ignoreUnknownKeys = true; explicitNulls = false; coerceInputValues = true }

        // Product License Setup // TODO move into composable
        Button({ viewModel.toggleAddProductLicenseModal() }) {
            Text("Add Product License")
        }
        val licenses = uiState.formValues["productLicenses"]
            ?.let { it as? kotlinx.serialization.json.JsonArray }
            ?.mapNotNull {
                runCatching {
                    json.decodeFromJsonElement(InternalProductLicenseDTO.serializer(), it)
                }.getOrNull()
            }
            ?: emptyList()

        FlowRow {
            licenses.forEach { license ->
                ProductLicenseCard(license, uiState.internalProducts, viewModel::deleteProductLicense)
            }
        }



        Button(onClick = { viewModel.handleSubmitButton(onSubmitSuccess) }) { Text("Update") }
    }

    if (uiState.showAddProductLicenseModal) {
        AddProductLicenseModal(uiState, viewModel)
    }
}

@Composable
private fun ProductLicenseCard(
    license: InternalProductLicenseDTO,
    products: List<InternalProductDTO>,
    deleteProductLicense: (String) -> Unit,
) {

    val productStr = products.find { it.uuid == license.productUUID }?.productName ?: "Unknown Product"

    Card(border = BorderStroke(1.dp, Color.Gray)) {
        Text("Product License Key ${license.uuid}")
        Text("Product Name: ${productStr}")
        Text("Billing Start Date: ${license.billingStartDate}")
        Text("Price: ${license.priceCents} cents")
        Text("Billing Cycle: ${license.billingCycle}")

        Button({ license.uuid?.let { deleteProductLicense(it) } }) {
            Text("Delete")
        }
    }
}

@Composable
private fun AddProductLicenseModal(uiState: CreateEditClientUiState, viewModel: CreateEditClientViewModel) {

    var expanded by remember { mutableStateOf(false) }
    var expanded2 by remember { mutableStateOf(false) }

    var billingStartDate by remember { mutableStateOf(Clock.System.now().toEpochMilliseconds()) }
    var selectedProduct by remember { mutableStateOf<InternalProductDTO?>(null) }
    var showBillingDatePicker by remember { mutableStateOf(false) }

    var priceDollarsTextFieldState by remember { mutableStateOf(TextFieldState("0.00")) }
    var selectedBillingCycle by remember { mutableStateOf(BillingCycle.MONTHLY) }

    BasicAlertDialog({ viewModel.toggleAddProductLicenseModal() }) {
        Surface {
            Column {

                // Select Product
                ExposedDropdownMenuBox(
                    expanded = expanded,
                    onExpandedChange = { expanded = it },
                ) {
                    OutlinedTextField(
                        value = selectedProduct?.productName ?: "Select a product",
                        onValueChange = {},
                        readOnly = true,
                        label = { Text("Product") },
                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expanded) },
                        modifier = Modifier.menuAnchor(),
                    )
                    ExposedDropdownMenu(
                        expanded = expanded,
                        onDismissRequest = { expanded = false },
                    ) {
                        uiState.internalProducts.forEach { product ->
                            DropdownMenuItem(
                                text = { Text(product.productName ?: product.uuid) },
                                onClick = {
                                    selectedProduct = product
                                    expanded = false
                                },
                            )
                        }
                    }
                }

                // Setup Pricing Plan
                Text("Pricing")

                // Billing Cycle
                TextField(
                    priceDollarsTextFieldState,
                    label = { Text("Billing Price") },
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                    inputTransformation = decimalInputTransformation,
                    outputTransformation = twoDecimalOutputTransformation
                )

                ExposedDropdownMenuBox(
                    expanded = expanded2,
                    onExpandedChange = { expanded2 = it },
                ) {
                    OutlinedTextField(
                        value = selectedBillingCycle.name ?: "Select a product",
                        onValueChange = {},
                        readOnly = true,
                        label = { Text("Billing Cycle") },
                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expanded2) },
                        modifier = Modifier.menuAnchor(),
                    )
                    ExposedDropdownMenu(
                        expanded = expanded2,
                        onDismissRequest = { expanded2 = false },
                    ) {
                        BillingCycle.entries.forEach { billingCycle ->
                            DropdownMenuItem(
                                text = { Text(billingCycle.name) },
                                onClick = {
                                    selectedBillingCycle = billingCycle
                                    expanded2 = false
                                },
                            )
                        }
                    }
                }


                Button({ showBillingDatePicker = true }) {
                    Text("Billing Start Date: ${billingStartDate.tryFormatAsDateTime()}")
                }

                // Add License Button
                Button({

                    val priceCents = priceDollarsTextFieldState.text.toString().dollarStringToCents()
                        ?: return@Button

                    val license = InternalProductLicenseDTO(
                        uuid = generateUUIDv7(),
                        productUUID = selectedProduct?.uuid ?: return@Button,
                        billingStartDate = billingStartDate,
                        priceCents = priceCents,
                        billingCycle = selectedBillingCycle,
                        createdAt = Clock.System.now().toEpochMilliseconds(),
                        enabled = true,
                    )

                    viewModel.addLicense(license)

                }) {
                    Text("Add License")
                }

                // Close Dialog
                Button(onClick = viewModel::toggleAddProductLicenseModal) {
                    Text("Close")
                }

            }

            if (showBillingDatePicker) {
                DateTimeEntryModal(
                    initialEpochMillis = billingStartDate,
                    title = "Select Billing Start Date",
                    onDismiss = {
                        showBillingDatePicker = false
                    },
                    onConfirm = { epochMillis ->
                        billingStartDate = epochMillis
                        showBillingDatePicker = false
                    },
                )
            }
        }
    }
}

class CreateEditClientViewModel : ViewModel() {

    private val _uiState = MutableStateFlow(CreateEditClientUiState())
    val uiState = _uiState.asStateFlow()
    val ktClient = KtorClient.instance.client
    val scope = CoroutineScope(Dispatchers.Default)


    fun loadProducts() {
        scope.launch {
            val response = ktClient.get("/admin/internal-products/list")
            val products = response.body<List<InternalProductDTO>>()
            _uiState.value = _uiState.value.copy(internalProducts = products)
        }
    }


    fun handleSubmitButton(onSuccess: () -> Unit = {}) {
        scope.launch {
            try {
                // Build the DTO from the current form values
                val json = Json { ignoreUnknownKeys = true; explicitNulls = false; isLenient = true; coerceInputValues = true }

                // Some values must be set before the object can be serialized
                val mergedValues = uiState.value.formValues.toMutableMap().also {
//                    it["accountSub"] = JsonPrimitive("TODO")
                }

                val payload = JsonObject(mergedValues)
                val body: ParentCompany = json.decodeFromJsonElement(ParentCompany.serializer(), payload)

                val response: HttpResponse? = if (uiState.value.editMode) {
                    ktClient.put("/admin/internal-clients") {
                        contentType(ContentType.Application.Json)
                        setBody(body)
                    }
                } else {
                    ktClient.post("/admin/internal-clients") {
                        contentType(ContentType.Application.Json)
                        setBody(body)
                    }
                }

                if (response != null && response.status.isSuccess()) {
                    _uiState.value = _uiState.value.copy(errorMessage = null)
                    onSuccess()
                } else {
                    _uiState.value = _uiState.value.copy(
                        errorMessage = "Failed to create or update client" +
                                (response?.status?.let { " (${it})" } ?: "")
                    )
                }

            } catch (e: Exception) {
                println("Error creating internal client: ${e.message}")
                _uiState.value = _uiState.value.copy(errorMessage = e.message)
            }
        }
    }

    fun onFieldsChanged(newValues: Map<String, JsonElement>) {
        _uiState.value = _uiState.value.copy(
            formValues = newValues,
            errorMessage = null,
        )
    }

    /** Onboarding: attach a new (blank) client to an existing account. Always creates (POST). */
    fun setAccountSub(accountSub: String) {
        _uiState.update { state ->
            state.copy(
                formValues = state.formValues.toMutableMap().also {
                    it["accountSub"] = JsonPrimitive(accountSub)
                },
                editMode = false,
            )
        }
    }

    /** Edit: populate the form from an already-loaded client. Always updates (PUT). */
    fun initForEdit(client: ParentCompany) {
        _uiState.update {
            it.copy(
                formValues = serializableToMap(ParentCompany.serializer(), client),
                editMode = true,
            )
        }
    }


    fun toggleAddProductLicenseModal() {
        _uiState.update { it.copy(showAddProductLicenseModal = !it.showAddProductLicenseModal) }
    }

    fun addLicense(license: InternalProductLicenseDTO) {
        _uiState.update { currentState ->
            val json = Json { ignoreUnknownKeys = true; explicitNulls = false }
            val licenseJson = json.encodeToJsonElement(InternalProductLicenseDTO.serializer(), license)

            val existingLicenses = currentState.formValues["productLicenses"]
                ?.let { it as? kotlinx.serialization.json.JsonArray }
                ?: kotlinx.serialization.json.JsonArray(emptyList())

            val updatedLicenses = kotlinx.serialization.json.JsonArray(existingLicenses + licenseJson)

            currentState.copy(
                formValues = currentState.formValues.toMutableMap().also {
                    it["productLicenses"] = updatedLicenses
                }
            )
        }
    }

    fun deleteProductLicense(uuid: String) {
        _uiState.update { currentState ->
            val json = Json { ignoreUnknownKeys = true; explicitNulls = false }
            val updatedLicenses = currentState.formValues["productLicenses"]
                ?.let { it as? JsonArray }
                ?.filter { element ->
                    val licenseUuid = (element as? kotlinx.serialization.json.JsonObject)
                        ?.get("uuid")
                        ?.let { (it as? JsonPrimitive)?.content }
                    licenseUuid != uuid
                }
                ?.let { JsonArray(it) }
                ?: JsonArray(emptyList())

            currentState.copy(
                formValues = currentState.formValues.toMutableMap().also {
                    it["productLicenses"] = updatedLicenses
                }
            )
        }
    }

}